"use client"

import React from "react"
import { X, MapPin, Phone, Mail, Star, Car, CreditCard, Clock, CheckCircle, XCircle } from "lucide-react"
import { Dialog, Flex, Box, Heading, Text, Badge, ScrollArea, Button } from "@radix-ui/themes"

interface Captain {
  _id: string
  name: string
  email: string
  phone: string
  profileImage: string
  isApproved: boolean
  isActive: boolean
  isOnline: boolean
  rating: number
  totalDeliveries: number
  totalEarnings: number
  completionRate: number
  vehicleType: string
  vehicleNumber: string
  drivingLicense: string
  vehicleRegistration: string
  insurance: string
  bankDetails: {
    accountNumber: string
    routingNumber: string
    accountHolderName: string
  }
  currentLocation: {
    latitude: number
    longitude: number
    address: string
  }
  workingHours: {
    [key: string]: { start: string; end: string; isWorking: boolean }
  }
  createdAt: string
}

interface CaptainDetailsModalProps {
  captain: Captain
  onClose: () => void
}

const CaptainDetailsModal: React.FC<CaptainDetailsModalProps> = ({ captain, onClose }) => {
  const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]

  return (
    <Dialog.Root open={true} onOpenChange={onClose}>
      <Dialog.Content style={{ maxWidth: 900 }}>
        <Dialog.Title>Captain Details</Dialog.Title>
        
        <Dialog.Description size="2" mb="4">
          View detailed information about {captain.name}
        </Dialog.Description>

        <ScrollArea style={{ maxHeight: "70vh" }} scrollbars="vertical">
          <Box>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Profile and Basic Info */}
            <div className="lg:col-span-1">
              <img
                src={captain.profileImage || "/placeholder-avatar.jpg"}
                alt={captain.name}
                className="w-full h-48 object-cover rounded-lg mb-4"
              />

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{captain.name}</h3>
                  <div className="flex gap-2">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        captain.isOnline ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {captain.isOnline ? "Online" : "Offline"}
                    </span>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        captain.isApproved ? "bg-blue-100 text-blue-800" : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {captain.isApproved ? "Approved" : "Pending"}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" />
                                                            <span className="text-sm text-gray-600 dark:text-gray-300">
                       {typeof captain.rating === 'object' && (captain.rating as any)?.average 
                         ? `${(captain.rating as any).average.toFixed(1)} Rating (${(captain.rating as any).count || 0} reviews)`
                         : typeof captain.rating === 'number' 
                           ? `${captain.rating.toFixed(1)} Rating`
                           : '0.0 Rating'
                       }
                      </span>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">{captain.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">{captain.phone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-300">
                      {captain.currentLocation?.address || "Location not available"}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm">
                  {captain.isActive ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500" />
                  )}
                  <span className="text-gray-600 dark:text-gray-300">
                    {captain.isActive ? "Active Account" : "Inactive Account"}
                  </span>
                </div>
              </div>
            </div>

            {/* Details and Stats */}
            <div className="lg:col-span-2 space-y-6">
              {/* Performance Stats */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3">Performance Statistics</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{captain.totalDeliveries}</div>
                    <div className="text-sm text-gray-500">Total Deliveries</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">SDG {captain.totalEarnings}</div>
                    <div className="text-sm text-gray-500">Total Earnings</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{captain.completionRate}%</div>
                    <div className="text-sm text-gray-500">Completion Rate</div>
                  </div>
                </div>
              </div>

              {/* Vehicle Information */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Car className="w-4 h-4" />
                  Vehicle Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-500">Vehicle Type</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                      {captain.vehicleType}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Vehicle Number</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{captain.vehicleNumber}</div>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Driving License:</span>
                    <a
                      href={captain.drivingLicense}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      View Document
                    </a>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Vehicle Registration:</span>
                    <a
                      href={captain.vehicleRegistration}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      View Document
                    </a>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Insurance:</span>
                    <a
                      href={captain.insurance}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      View Document
                    </a>
                  </div>
                </div>
              </div>

              {/* Bank Details */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Bank Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-500">Account Holder</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {captain.bankDetails.accountHolderName}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Account Number</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      ****{captain.bankDetails.accountNumber.slice(-4)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Routing Number</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {captain.bankDetails.routingNumber}
                    </div>
                  </div>
                </div>
              </div>

              {/* Working Hours */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Working Hours
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {days.map((day) => (
                    <div key={day} className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{day}</span>
                      {captain.workingHours[day]?.isWorking ? (
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {captain.workingHours[day].start} - {captain.workingHours[day].end}
                        </span>
                      ) : (
                        <span className="text-sm text-red-500">Not Working</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Information */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3">Additional Information</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Joined:</span>
                    <span className="text-gray-900 dark:text-white">
                      {new Date(captain.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Captain ID:</span>
                    <span className="text-gray-900 dark:text-white font-mono text-xs">{captain._id}</span>
                  </div>
                  {captain.currentLocation && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Latitude:</span>
                        <span className="text-gray-900 dark:text-white">{captain.currentLocation.latitude}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Longitude:</span>
                        <span className="text-gray-900 dark:text-white">{captain.currentLocation.longitude}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Box>
        </ScrollArea>
        
        <Flex gap="3" mt="6" pt="6" justify="end" style={{ borderTop: "1px solid var(--gray-5)" }}>
          <Dialog.Close>
            <Button variant="soft" color="gray">
              Close
            </Button>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default CaptainDetailsModal
